import * as React from "react";
import { useEffect, useRef } from "react";
import { AmbientLight, BufferGeometry, DirectionalLight, Mesh, MeshNormalMaterial, PerspectiveCamera, Scene, Sphere, Vector3, WebGLRenderer } from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
/**
 * Renders a collider's geometry in an isolated three.js scene with a normal material and orbit controls, so a
 * degenerate/empty/misplaced collider can be inspected visually instead of just via vertex counts in the console.
 * The geometry is cloned rather than referenced directly, since the live CollisionShape it belongs to can be
 * disposed/replaced by CollisionWorld at any time (e.g. compound rebuilds) while this preview is still showing.
 */
export function ColliderGeometryPreview({ geometry }) {
    const canvasRef = useRef(null);
    const meshRef = useRef(null);
    const cameraRef = useRef(null);
    const controlsRef = useRef(null);
    const hasFramedCameraRef = useRef(false);
    // Mount once - scene/camera/renderer/controls/render loop stay alive across geometry updates so re-orbiting the
    // camera isn't reset every time the polled snapshot hands us a new geometry instance for the same collider.
    useEffect(() => {
        var _a;
        const canvas = canvasRef.current;
        if (canvas === null)
            return;
        const scene = new Scene();
        const camera = new PerspectiveCamera(50, 1, 0.01, 1000);
        const renderer = new WebGLRenderer({ canvas, antialias: true });
        renderer.setClearColor(0xffffff);
        const controls = new OrbitControls(camera, canvas);
        controls.enableDamping = true;
        scene.add(new AmbientLight(0xffffff, 0.6));
        const light = new DirectionalLight(0xffffff, 0.8);
        light.position.set(2, 4, 3);
        scene.add(light);
        const mesh = new Mesh(new BufferGeometry(), new MeshNormalMaterial());
        scene.add(mesh);
        meshRef.current = mesh;
        cameraRef.current = camera;
        controlsRef.current = controls;
        camera.position.set(1, 1, 1);
        controls.update();
        let animationFrame;
        const render = () => {
            controls.update();
            renderer.render(scene, camera);
            animationFrame = requestAnimationFrame(render);
        };
        render();
        const resize = () => {
            const parent = canvas.parentElement;
            if (parent === null)
                return;
            const width = parent.clientWidth;
            const height = parent.clientHeight;
            renderer.setSize(width, height, false);
            camera.aspect = width / (height || 1);
            camera.updateProjectionMatrix();
        };
        const resizeObserver = new ResizeObserver(resize);
        resizeObserver.observe((_a = canvas.parentElement) !== null && _a !== void 0 ? _a : canvas);
        resize();
        return () => {
            var _a;
            cancelAnimationFrame(animationFrame);
            resizeObserver.disconnect();
            controls.dispose();
            renderer.dispose();
            mesh.geometry.dispose();
            mesh.material.dispose();
            meshRef.current = null;
            cameraRef.current = null;
            controlsRef.current = null;
            // renderer.dispose() frees GPU resources but does NOT release the underlying WebGL context slot - that only
            // happens on GC, which isn't synchronous. Since this component mounts a new context every time a different
            // collider is expanded, rapidly switching colliders exhausts the browser's context limit (~16) before old
            // contexts are collected, forcing the browser to lose contexts - including the main app's - and leaving the
            // canvas white. Explicitly losing the context here frees the slot immediately.
            (_a = renderer.getContext().getExtension("WEBGL_lose_context")) === null || _a === void 0 ? void 0 : _a.loseContext();
        };
    }, []);
    // Geometry updates - swap in a clone of the latest geometry without touching the camera/controls state, except
    // the very first time (nothing meaningful to preserve yet), where the camera is framed on the bounding sphere.
    useEffect(() => {
        var _a;
        const mesh = meshRef.current;
        const camera = cameraRef.current;
        const controls = controlsRef.current;
        if (mesh === null || camera === null || controls === null)
            return;
        // Not geometry.clone(): the live geometry can be a BVHBufferGeometry, whose constructor requires a source
        // geometry argument - three.js's default clone() calls `new this.constructor()` with no arguments, which throws.
        // Copying attributes into a plain BufferGeometry sidesteps that subclass entirely.
        const nextGeometry = new BufferGeometry().copy(geometry);
        // Raw collider mesh/hull geometry only carries positions (see createColliderMeshGeometry) - MeshNormalMaterial
        // needs normals to shade correctly, so make sure they exist regardless of where the geometry came from.
        if (nextGeometry.getAttribute("normal") === undefined) {
            nextGeometry.computeVertexNormals();
        }
        mesh.geometry.dispose();
        mesh.geometry = nextGeometry;
        if (!hasFramedCameraRef.current) {
            hasFramedCameraRef.current = true;
            nextGeometry.computeBoundingSphere();
            const boundingSphere = (_a = nextGeometry.boundingSphere) !== null && _a !== void 0 ? _a : new Sphere(new Vector3(), 1);
            const radius = boundingSphere.radius || 1;
            camera.position.copy(boundingSphere.center).add(new Vector3(radius * 2, radius * 1.5, radius * 2));
            camera.near = Math.max(radius / 100, 0.001);
            camera.far = radius * 100;
            camera.updateProjectionMatrix();
            controls.target.copy(boundingSphere.center);
            controls.update();
        }
    }, [geometry]);
    return React.createElement("canvas", { ref: canvasRef, style: { width: "100%", height: "100%", display: "block" } });
}
